import { useEffect, useState } from 'react';
import { SelectOption } from '../components/SiniestrosFilters/types';

import useContracts from '../../../components/layout/ContractSelector/hook/useContracts';
import useOrganizationSelector from '../../../components/layout/OrganizationSelector/hook/useOrganizationSelector';
import { useGetEmpresasQuery } from '../../../services/companyApi';
import { useGetProvincesQuery } from '../../../services/companyApi';
import { useGetPreventorsQuery } from '../../../services/visitasApi';
import { formatFullName } from '../../../utils/common';

interface UseSiniestrosFiltersResult {
    clients: SelectOption[];
    contracts: SelectOption[];
    establishments: SelectOption[];
    provinces: SelectOption[];
    preventors: SelectOption[];
    isLoading: boolean;
}

// El supervisor no tiene permiso sobre el listado de preventores ni usa ese filtro (SMP-460)
const useSiniestrosFilters = ({
    omitirPreventores = false
}: { omitirPreventores?: boolean } = {}): UseSiniestrosFiltersResult => {
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const [clients, setClients] = useState<SelectOption[]>([]);
    const [contractsOptions, setContractsOptions] = useState<SelectOption[]>([]);
    const [establishments, setEstablishments] = useState<SelectOption[]>([]);
    const [provinces, setProvinces] = useState<SelectOption[]>([]);
    const [preventorsOptions, setPreventorsOptions] = useState<SelectOption[]>([]);

    const { contracts } = useContracts();
    const { organizations: organizationsOptions, isFetching: isFetchingOrganizations } =
        useOrganizationSelector();

    useEffect(() => {
        if (contracts) {
            setContractsOptions(
                contracts.map((c: any) => ({
                    id: c.id,
                    label: c.nombre
                }))
            );
        }
    }, [contracts]);

    useEffect(() => {
        if (organizationsOptions?.length) {
            setClients(
                organizationsOptions.map((organization: any) => ({
                    id: organization.id,
                    label: organization.nombre
                }))
            );
        }
    }, [organizationsOptions]);

    const { data: establishmentsData, isFetching: isFetchingEstablishments } = useGetEmpresasQuery({
        skip: 0,
        take: 5000
    });

    useEffect(() => {
        if (establishmentsData?.data?.data) {
            setEstablishments(
                establishmentsData.data.data.map((est: any) => ({
                    id: est.id,
                    label: `${est.nombre} (${est.codigoSRT})`,
                    extra: { establishmentName: est.nombre }
                }))
            );
        }
    }, [establishmentsData]);

    const { data: provincesList } = useGetProvincesQuery(null);

    useEffect(() => {
        if (provincesList?.data?.provincias) {
            setProvinces(
                provincesList.data.provincias.map((prov: any) => ({
                    id: prov.id,
                    label: prov.nombre
                }))
            );
        }
    }, [provincesList]);

    const { data: preventorsData, isFetching: isFetchingPreventors } = useGetPreventorsQuery(
        { take: 9999999, skip: 0 },
        { skip: omitirPreventores }
    );

    useEffect(() => {
        if (preventorsData?.data) {
            setPreventorsOptions(
                preventorsData.data.map((p: any) => ({
                    id: p.idUsuario,
                    label: formatFullName(p.nombre, p.apellido)
                }))
            );
        }
    }, [preventorsData]);

    useEffect(() => {
        setIsLoading(isFetchingOrganizations || isFetchingEstablishments || isFetchingPreventors);
    }, [isFetchingOrganizations, isFetchingEstablishments, isFetchingPreventors]);

    return {
        clients,
        contracts: contractsOptions,
        establishments,
        provinces,
        preventors: preventorsOptions,
        isLoading
    };
};

export default useSiniestrosFilters;
